<template>
  <div class="sankey-container" ref="sankeyContainer">
    <div class="flex justify-end">
      <ExportMenu class="absolute z-10" :exports="[{ type: 'chart', action: downloadSVG }]" />
    </div>
    <svg ref="sankeyChart" class="sankey-svg"></svg>
    <div ref="tooltip" class="tooltip"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import { sankey, sankeyLinkHorizontal } from 'd3-sankey'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { blockSensitiveEgress } from '@/utils/demoEgress'

export default {
  name: 'SankeyDiagram',
  components: { ExportMenu },
  props: {
    chartData: {
      type: Object,
      required: true,
      validator: (value) => {
        return value &&
               Array.isArray(value.nodes) &&
               Array.isArray(value.links) &&
               Array.isArray(value.stageLabels)
      }
    },
    config: {
      type: Object,
      default: () => ({
        width: 800,
        height: 500,
        margin: { top: 40, right: 150, bottom: 40, left: 50 },
        nodeWidth: 15,
        nodePadding: 20,
        iterations: 32,
        linkOpacity: 0.6,
        formatting: {
          percentage: false,
          sig_figs: 1
        }
      })
    }
  },
  data() {
    return {
      chart: null,
      svg: null,
      sankeyGenerator: null,
      resizeTimer: null,
      resizeObserver: null,
      currentWidth: 0,
      currentHeight: 0,
      tooltip: null
    }
  },
  mounted() {
    this.initChart()
    this.setupResizeObserver()
  },
  beforeUnmount() {
    this.cleanupResizeObserver()
  },
  watch: {
    chartData: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    config: {
      handler() {
        this.initChart()
      },
      deep: true
    }
  },
  methods: {
    setupResizeObserver() {
      const container = this.$refs.sankeyContainer
      if (!container) return

      // Use ResizeObserver for efficient container size detection
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect
          // Only reinit if size actually changed significantly (avoid loops)
          if (Math.abs(width - this.currentWidth) > 5 || Math.abs(height - this.currentHeight) > 5) {
            this.handleResize()
          }
        }
      })
      this.resizeObserver.observe(container)
    },
    cleanupResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
    },
    formatDataValue(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "N/A";
      }

      if (this.config.formatting?.percentage) {
        return d3.format(",.1%")(value);
      }

      // Default: thousands separator, no decimals
      return d3.format(",")(Math.round(value));
    },
    handleResize() {
      // Debounce the resize for better performance
      if (this.resizeTimer) clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.initChart()
      }, 150)
    },
    initChart() {
      const container = this.$refs.sankeyContainer
      if (!container) return

      // Clear previous chart
      d3.select(this.$refs.sankeyChart).selectAll('*').remove()
      
      // Initialize tooltip reference
      this.tooltip = d3.select(this.$refs.tooltip)

      // Calculate dimensions using container's actual size, with fallbacks
      const width = container.clientWidth || this.config.width || 800
      const height = container.clientHeight || this.config.height || 500
      const margin = this.config.margin || { top: 40, right: 150, bottom: 40, left: 50 }
      const innerWidth = Math.max(width - margin.left - margin.right, 200)
      const innerHeight = Math.max(height - margin.top - margin.bottom, 200)

      // Create SVG with viewBox for responsive scaling
      // Use 'none' to stretch and fill the container completely
      const svgEl = d3.select(this.$refs.sankeyChart)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'none')

      this.svg = svgEl
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`)

      // Create Sankey generator
      this.sankeyGenerator = sankey()
        .nodeWidth(this.config.nodeWidth)
        .nodePadding(this.config.nodePadding)
        .extent([[0, 0], [innerWidth, innerHeight]])
        .iterations(this.config.iterations)

      // Store current dimensions for later use
      this.currentWidth = width
      this.currentHeight = height

      this.updateChart()
    },
    updateChart() {
      if (!this.svg || !this.chartData || !this.chartData.nodes || !this.chartData.links) return

      // Deep copy the data to avoid mutating props
      const data = {
        nodes: JSON.parse(JSON.stringify(this.chartData.nodes)),
        links: JSON.parse(JSON.stringify(this.chartData.links))
      }

      // Get current dimensions from computed properties
      const innerWidth = this.innerWidth
      const innerHeight = this.innerHeight

      // Update the Sankey generator with current dimensions
      this.sankeyGenerator
        .extent([[0, 0], [innerWidth, innerHeight]])

      // Generate the Sankey layout
      const {nodes, links} = this.sankeyGenerator(data)

      // Clear previous elements
      this.svg.selectAll('.link, .node, .node-label, .stage-label, defs').remove()

      // --- Add gradients for links ---
      const defs = this.svg.append('defs')
      links.forEach((d, i) => {
        // fallback colors if not set
        const sourceColor = d.source.color || '#aaa'
        const targetColor = d.target.color || '#aaa'
        const grad = defs.append('linearGradient')
          .attr('id', `link-gradient-${i}`)
          .attr('gradientUnits', 'userSpaceOnUse')
          .attr('x1', d.source.x1)
          .attr('x2', d.target.x0)
          .attr('y1', (d.y0 + d.y1) / 2)
          .attr('y2', (d.y0 + d.y1) / 2)
        grad.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', sourceColor)
        grad.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', targetColor)
      })

      // Draw the links
      const link = this.svg.append('g')
        .attr('class', 'links')
        .attr('fill', 'none')
        .selectAll('path')
        .data(links)
        .enter()
        .append('path')
        .attr('class', 'link')
        .attr('d', sankeyLinkHorizontal())
        .attr('stroke', (d, i) => `url(#link-gradient-${i})`)
        .attr('stroke-width', d => Math.max(1, d.width))
        .attr('stroke-opacity', this.config.linkOpacity)
        .style('fill', 'none')
        .style('cursor', 'pointer')
        .sort((a, b) => b.width - a.width)

      // Add link hover effects, tooltips, and click handler
      const tooltipEl = this.$refs.sankeyContainer.querySelector('.tooltip')
      link.on('mouseover', (event, d) => {
          d3.select(event.currentTarget)
            .attr('stroke-opacity', this.config.linkOpacity + 0.2)
            .attr('stroke-width', d => Math.max(1, d.width + 2))

          tooltipEl.style.visibility = 'visible'
          tooltipEl.style.top = `${event.clientY - 10}px`
          tooltipEl.style.left = `${event.clientX + 10}px`
          tooltipEl.innerHTML = `<strong>${d.source.name} → ${d.target.name}</strong><br>Value: ${this.formatDataValue(d.value)}`
        })
        .on('mousemove', (event) => {
          tooltipEl.style.top = `${event.clientY - 10}px`
          tooltipEl.style.left = `${event.clientX + 10}px`
        })
        .on('mouseout', (event) => {
          d3.select(event.currentTarget)
            .attr('stroke-opacity', this.config.linkOpacity)
            .attr('stroke-width', d => Math.max(1, d.width))

          tooltipEl.style.visibility = 'hidden'
        })
        .on('click', (event, d) => {
          this.$emit('link-click', {
            source: d.source.index,
            target: d.target.index,
            value: d.value,
            sourceName: d.source.name,
            targetName: d.target.name
          })
        })

      // Draw the nodes
      const node = this.svg.append('g')
        .attr('class', 'nodes')
        .selectAll('.node')
        .data(nodes)
        .enter()
        .append('g')
        .attr('class', 'node')
        .attr('transform', d => `translate(${d.x0},${d.y0})`)

      // Add node rectangles
      node.append('rect')
        .attr('height', d => d.y1 - d.y0)
        .attr('width', d => d.x1 - d.x0)
        .attr('fill', d => d.color || '#69b3a2')
        .attr('stroke', '#000')
        .style('cursor', 'pointer')
        .on('mouseover', (event, d) => {
          d3.select(event.currentTarget)
            .attr('stroke-width', 2)

          tooltipEl.style.visibility = 'visible'
          tooltipEl.style.top = `${event.clientY - 10}px`
          tooltipEl.style.left = `${event.clientX + 10}px`
          tooltipEl.innerHTML = `<strong>${d.name}</strong><br>Value: ${this.formatDataValue(d.value)}`
        })
        .on('mousemove', (event) => {
          tooltipEl.style.top = `${event.clientY - 10}px`
          tooltipEl.style.left = `${event.clientX + 10}px`
        })
        .on('mouseout', (event) => {
          d3.select(event.currentTarget)
            .attr('stroke-width', 1)

          tooltipEl.style.visibility = 'hidden'
        })
        .on('click', (event, d) => {
          this.$emit('node-click', {
            index: d.index,
            name: d.name,
            value: d.value
          })
        })

      // Add node labels
      const labelFontSize = (this.config?.labelFontSize || 12) + 'px'
      node.append('text')
        .attr('class', 'node-label')
        .attr('x', d => (d.x0 < innerWidth / 2) ? (d.x1 - d.x0 + 6) : -6)
        .attr('y', d => (d.y1 - d.y0) / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', d => (d.x0 < innerWidth / 2) ? 'start' : 'end')
        .text(d => d.name)
        .style('font-size', labelFontSize)
        .style('pointer-events', 'none')
        .call(this.wrapText, 120)

      // Add stage labels if available
      if (this.chartData.stageLabels) {
        const stageLabelHeight = 20

        this.svg.selectAll('.stage-label')
          .data(this.chartData.stageLabels)
          .enter()
          .append('text')
          .attr('class', 'stage-label')
          .attr('x', d => {
            // Support numeric positions (0-1) or named positions
            if (typeof d.position === 'number') return innerWidth * d.position
            if (d.position === 'left') return 0
            if (d.position === 'right') return innerWidth
            return innerWidth / 2
          })
          .attr('y', -stageLabelHeight)
          .attr('text-anchor', d => {
            if (typeof d.position === 'number') {
              if (d.position <= 0.1) return 'start'
              if (d.position >= 0.9) return 'end'
              return 'middle'
            }
            if (d.position === 'left') return 'start'
            if (d.position === 'right') return 'end'
            return 'middle'
          })
          .style('font-size', '14px')
          .style('font-weight', 'bold')
          .text(d => d.label)
      }
    },
    wrapText(text, width) {
      text.each(function() {
        const text = d3.select(this)
        const words = text.text().split(/\n/)
        const lineHeight = 1.1
        let lineNumber = 0

        const y = text.attr('y')
        const dy = parseFloat(text.attr('dy'))
        const textAnchor = text.attr('text-anchor')

        text.text(null)

        words.forEach((word, i) => {
          text.append('tspan')
            .attr('x', text.attr('x'))
            .attr('y', y)
            .attr('dy', `${i === 0 ? dy : lineHeight}em`)
            .attr('text-anchor', textAnchor)
            .text(word)
        })
      })
    },
    downloadSVG() {
      const el = this.$refs.sankeyChart
      if (!el) return
      if (blockSensitiveEgress('export')) return
      const serializer = new XMLSerializer()
      let source = serializer.serializeToString(el)
      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"')
      }
      const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'sankey_diagram.svg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  },
  computed: {
    innerWidth() {
      const container = this.$refs.sankeyContainer
      const width = (container && container.clientWidth) || this.config.width || 800
      const margin = this.config.margin || { top: 40, right: 150, bottom: 40, left: 50 }
      return Math.max(width - margin.left - margin.right, 200)
    },
    innerHeight() {
      const container = this.$refs.sankeyContainer
      const height = (container && container.clientHeight) || this.config.height || 500
      const margin = this.config.margin || { top: 40, right: 150, bottom: 40, left: 50 }
      return Math.max(height - margin.top - margin.bottom, 200)
    }
  }
}
</script>

<style scoped>
.sankey-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}

.sankey-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.tooltip {
  position: fixed;
  visibility: hidden;
  background: #fff;
  border: 1px solid #ccc;
  padding: 5px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  pointer-events: none;
  font-size: 12px;
  z-index: 10000;
}
</style>
